'use client'

import {observer} from 'mobx-react'
import React from 'react'
import {useFormContext} from 'react-hook-form'
import {toast} from 'react-toastify'
import {z} from 'zod'
import {useMutation, useQueryClient} from '@tanstack/react-query'

import {Routes} from '@/src/config/routes'
import {zFormRules} from '@/src/config/validation'

import Button from '@/src/shared/ui/button'
import {FormCheckBox} from '@/src/shared/ui/inputs/checkbox'
import {getClient} from '@/src/shared/utils/api-factory'

import {ContactFields, ContactFieldsData} from './contact-fields'
import * as UI from './form.styled'
import {PasswordFieldsData} from './password-fields'
import {PersonalFields, PersonalFieldsData} from './personal-fields'
import {ShipmentFields, ShipmentFieldsData} from './shipment-fields'
import {checkRequirementUserRules} from '@/src/core/domain/entities/user'
import {Alert} from 'antd'
import styled from 'styled-components'
import {User} from '@/src/entities/user/types'
import {ProfileEvents} from '@/src/core/domain/events/profile.events'
import {getInjection} from '@runlab/di'
import {useRouter} from 'nextjs-toploader/app'
import {useLayoutStore} from '@/src/shared/ui/layout'
import {formatDateForAPIFormat} from '@/src/shared/utils/functions'
import {handleMutationException} from '@/src/shared/utils/error'
import {InvisibleCaptcha, useCaptcha} from '@/src/shared/modules/captcha'

export type PersonalFormData = PersonalFieldsData &
  ContactFieldsData &
  ShipmentFieldsData &
  Partial<PasswordFieldsData> & {
    captcha_token: string
  }

export const PersonalFormSchema = z.object({
  last_name: z.string().max(50, 'Слишком большое значение').min(1, 'Обязательное поле'),
  name: z.string().max(50, 'Слишком большое значение').min(1, 'Обязательное поле'),
  second_name: z.string().max(50, 'Слишком большое значение').nullable().optional(),

  email: zFormRules.email.refine(email => email && email.length > 0, 'Обязательное поле'),
  birthday: z.string().min(1, 'Обязательное поле'),

  address: z
    .object({
      city: z.string().max(100, 'Слишком большое значение').nullable().optional(),
      zip: z.string().max(10, 'Слишком большое значение').nullable().optional(),
      street: z.string().max(255, 'Слишком большое значение').nullable().optional(),
      house: z.string().max(5, 'Слишком большое значение').nullable().optional(),
      lobby: z.string().max(5, 'Слишком большое значение').nullable().optional(),
      housing: z.string().max(5, 'Слишком большое значение').nullable().optional(),
      building: z.string().max(5, 'Слишком большое значение').nullable().optional(),
      floor: z.string().max(3, 'Слишком большое значение').nullable().optional(),
      flat: z.string().max(5, 'Слишком большое значение').nullable().optional(),
      comment: z.string().nullable().optional(),
    })
    .optional(),

  agree: z.literal(true, {
    errorMap: () => ({
      message: 'Для продолжения необходимо принять условия пользовательского соглашения.',
    }),
  }),
})

interface PersonalFormProps {
  simple?: boolean
}

export const PersonalForm = observer(({simple}: PersonalFormProps) => {
  const form = useFormContext<PersonalFormData>()
    const {token, onSuccessToken, visibleCaptcha, setVisibleCaptcha, renderKey, reset} =
      useCaptcha()

  const router = useRouter()
  const eventBus = getInjection('IEventBus')

  const queryClient = useQueryClient()
  const layoutStore = useLayoutStore()

  const userFieldsIsFullfilled = checkRequirementUserRules(layoutStore.data.session?.user ?? null)

  const onSubmitMutation = useMutation({
    mutationFn: async (data: PersonalFormData) => {
      const client = getClient()
      const innerClient = getClient('api')

      const updatedUserData = await client.post<User>(Routes.webApi.profile.personal.edit, {
        data: {...data, birthday: formatDateForAPIFormat(data.birthday)},
      })

      await innerClient.post(Routes.api.auth.updateSession, {data: {user: updatedUserData}})
      await queryClient.invalidateQueries({queryKey: ['layout']})
    },
    async onSuccess() {
      eventBus.publish(ProfileEvents.formFilled())

      // TODO: вынести в интерфейс и слушать событие ProfileEvents.formFilled()
      const next = new URL(location.href).searchParams.get('next')

      if (next) {
        router.push(next)
      } else {
        router.refresh()
      }

      // TODO: слушать событие ProfileEvents.formFilled() и реагировать на него
      toast.success('Данные успешно изменены')
      // TODO: слушать событие ProfileEvents.formFilled() и реагировать на него
      queryClient.invalidateQueries()
    },
    async onError(error) {
      handleMutationException(error)
      reset()
    },
  })

  const submit = form.handleSubmit(data => {
    onSubmitMutation.mutate({...data, captcha_token: token!})
  })

  return (
    <>
      {!userFieldsIsFullfilled && (
        <StyledAlert
          type="warning"
          description="Для пользования сайтом необходимо заполнить обязательные поля."
        />
      )}

      <UI.Wrapper>
        <InvisibleCaptcha
          key={renderKey}
          renderKey={renderKey}
          onSuccess={onSuccessToken}
          visible={visibleCaptcha}
          setVisible={setVisibleCaptcha}
        />

        <UI.GridWrapper>
          <UI.PersonalFields as={PersonalFields} />
          <UI.ContactFields as={ContactFields} />
        </UI.GridWrapper>

        {!simple && (
          <UI.ShipmentForm>
            <UI.FormTitle>Адрес доставки:</UI.FormTitle>
            <UI.FormFields>
              <ShipmentFields />
            </UI.FormFields>
          </UI.ShipmentForm>
        )}

        <UI.Checkbox as={FormCheckBox} name="agree" required>
          Даю свое согласие на обработку{' '}
          <UI.AgreeLink
            href={layoutStore.data.forms.personal_data_consent_url}
            target="_blank"
            onClick={e => {
              e.stopPropagation()
            }}>
            Персональных дальнных
          </UI.AgreeLink>{' '}
          и соглашаюсь в{' '}
          <UI.AgreeLink
            href={layoutStore.data.forms.marketing_communications_consent_url}
            target="_blank"
            onClick={e => {
              e.stopPropagation()
            }}>
            пользовательским соглашением
          </UI.AgreeLink>{' '}
          на получение смс
        </UI.Checkbox>

        <UI.Button as={Button} onClick={submit} loading={onSubmitMutation.isPending}>
          Сохранить изменения
        </UI.Button>
      </UI.Wrapper>
    </>
  )
})

const StyledAlert = styled(Alert)`
  margin-bottom: 20px;
`
